import { isArray, get, set, each, isObject } from 'lodash-es';
import {
  ComponentSpec,
  PageSpec,
  ModalSpec,
  ComponentContext,
} from './schemas';
import * as YAML from 'yaml';
import { AxiosResponse } from 'axios';
import { ComponentPublicInstance } from 'vue';

export function isPageSpec(value: ComponentSpec): value is PageSpec {
  return value.kind === 'page';
}

export function isModalSpec(value: ComponentSpec): value is ModalSpec {
  return value.kind === 'modal';
}

export function isComponentContext(target: any): target is ComponentContext {
  return (
    target &&
    typeof target === 'object' &&
    ('$app' in target || '$root' in target)
  );
}

export function assertComponentContext(
  target: any,
): asserts target is ComponentContext {
  if (!isComponentContext(target)) {
    throw new Error('ComponentContext instance expected.');
  }
}

export function assertVueComponent(
  target: any,
): asserts target is ComponentPublicInstance {
  if (
    !target ||
    typeof target !== 'object' ||
    (!('$el' in target) && !('$root' in target) && !('$app' in target))
  ) {
    throw new Error('Vue component instance expected.');
  }
}

export function stripObject(target: any) {
  return JSON.parse(JSON.stringify(target));
}

export function toYamlString(
  value: any,
  options: {
    sortMapEntries: boolean;
  },
) {
  return YAML.stringify(value, options);
}

export function imageData(value: AxiosResponse | File) {
  if (value instanceof File) {
    return imageDataFromFile(value);
  }
  return imageDataFromAxiosResponse(value);
}

export function imageDataFromAxiosResponse(response: AxiosResponse) {
  let image = btoa(String.fromCharCode(...new Uint8Array(response.data)));
  const contentType = response.headers['content-type'].toLowerCase();
  return `data:${contentType};base64,${image}`;
}

export function imageDataFromFile(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result?.toString() || '');
    reader.onerror = (error) => reject(error);
  });
}

export function cyrb53(str: string, seed = 0) {
  let h1 = 0xdeadbeef ^ seed,
    h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

export type TraverseCallback = (
  v: any,
  k: string | number,
  obj: any,
) => void | boolean;

export function traverse(
  obj: any,
  cb: TraverseCallback,
  maxLevel: number | null = null,
) {
  const visited: string[] = [];

  doTraverse(obj, '.');

  function doTraverse(target: any, path: string, level = 0) {
    if (isArray(target)) {
      each(target, (v, i) => {
        const p = `${path}.${i}`;
        if (visited.indexOf(p) > -1) {
          return;
        }
        visited.push(p);

        let result = cb(v, i, target);

        if (result !== false) {
          if (level == maxLevel) {
            return;
          }
          doTraverse(v, p, level + 1);
        }
      });
      return;
    }

    if (isObject(target)) {
      each(target, (v, k) => {
        const p = `${path}.${k}`;
        if (visited.indexOf(p) > -1) {
          return;
        }
        visited.push(p);

        let result = cb(v, k, target);

        if (result !== false) {
          if (level == maxLevel) {
            return;
          }
          doTraverse(v, p, level + 1);
        }
      });
    }
  }
}

export function isDOMEvent(value: any): value is Event {
  return value && value.target instanceof EventTarget;
}

export function deepCopy(obj: any): any {
  let copy;

  if (null == obj || 'object' != typeof obj) return obj;

  if (obj instanceof Date) {
    copy = new Date();
    copy.setTime(obj.getTime());
    return copy;
  }

  if (obj instanceof Array) {
    copy = [];
    for (var i = 0, len = obj.length; i < len; i++) {
      copy[i] = deepCopy(obj[i]);
    }
    return copy;
  }

  if (obj instanceof Object) {
    copy = {};
    for (let attr in obj) {
      if (obj.hasOwnProperty(attr)) {
        set(copy, attr, deepCopy(obj[attr]));
      }
    }
    return copy;
  }

  console.error(obj);
  throw new Error("Unable to copy obj! Its type isn't supported.");
}
